import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, Building2, CheckCircle2, History } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * CinTypeahead
 * -----------
 * Smart CIN input for startup onboarding. Founders can either:
 *   - Paste a 21-char CIN directly (traditional path)
 *   - Type >=3 chars of the company name → dropdown of MCA candidates
 *
 * Selecting a candidate autofills CIN + surfaces incorporation date + status
 * to the parent form via the `onCandidate` callback.
 *
 * Props:
 *   value       — current CIN string
 *   onChange    — (cin: string) => void  (fires on manual typing)
 *   onCandidate — ({cin, company_legal_name, date_of_incorporation, ...}) => void
 *                 fires when the user picks a suggestion
 *   testId      — root data-testid
 */
export default function CinTypeahead({ value, onChange, onCandidate, testId = "cin-typeahead" }) {
  const [query, setQuery] = useState(value || "");
  const [candidates, setCandidates] = useState([]);
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  // Keep external `value` in sync (e.g. when parent form loads).
  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value || "");
    }
  }, [value]);

  // Load recent picks once — surfaced when dropdown opens with no query.
  useEffect(() => {
    api.get("/startup/mca-recent")
      .then((r) => setRecents(r.data?.items || []))
      .catch(() => setRecents([]));
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced search — trigger only when input doesn't look like a CIN.
  const runSearch = async (q) => {
    setLoading(true);
    try {
      const r = await api.post("/startup/mca-search", { query: q });
      setCandidates(r.data.results || []);
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const looksLikeCin = (v) => /^[UL][A-Z0-9]{20}$/i.test((v || "").trim());

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setSelectedLabel("");
    onChange?.(v);

    if (timerRef.current) clearTimeout(timerRef.current);
    if (!v || v.trim().length < 3 || looksLikeCin(v)) {
      setCandidates([]);
      // If the field just went empty and we have recents, surface them so the
      // founder's team can quickly pick a previously chosen company without
      // needing to blur+refocus.
      if (!v && recents.length > 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
      return;
    }
    timerRef.current = setTimeout(() => runSearch(v.trim()), 300);
  };

  const pickCandidate = async (c) => {
    setQuery(c.cin || "");
    setSelectedLabel(c.company_legal_name || "");
    setOpen(false);
    onChange?.(c.cin || "");
    const loadingId = toast.loading(`Fetching MCA master data for ${c.company_legal_name}…`);
    // Fire-and-forget persistence to recents (5 MRU).
    api.post("/startup/mca-recent", {
      cin: c.cin,
      company_legal_name: c.company_legal_name,
      date_of_incorporation: c.date_of_incorporation,
      registered_state: c.registered_state,
      company_status: c.company_status,
    }).then((r) => setRecents(r.data?.items || []))
      .catch(() => { /* non-fatal */ });
    // Fetch the full MCA master data (read-only, no state mutation) so the
    // parent form can autofill every profile field, not just the CIN + name.
    let enriched = c;
    let fieldsFilled = 4;
    if (c.cin) {
      try {
        const { data } = await api.post("/startup/mca-fetch", { cin: c.cin });
        if (data?.ok && data.company) {
          enriched = { ...c, ...data.company };
          fieldsFilled = Object.keys(data.company).filter((k) => data.company[k] != null && data.company[k] !== "").length;
        }
      } catch { /* fall back to lightweight candidate fields */ }
    }
    toast.dismiss(loadingId);
    toast.success(`Auto-filled ${fieldsFilled} fields from MCA for ${c.company_legal_name}.`);
    onCandidate?.(enriched);
  };

  const handleFocus = () => {
    if (candidates.length > 0) { setOpen(true); return; }
    // No live search yet — show recents if any.
    if (!query && recents.length > 0) setOpen(true);
  };

  return (
    <div ref={rootRef} className="block relative" data-testid={testId}>
      <div className="overline mb-1.5 flex items-center justify-between">
        <span>CIN / LLPIN</span>
        <span className="text-[10px] font-mono normal-case text-ink-muted">
          Type CIN or company name
        </span>
      </div>
      <div className="relative">
        <input
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="e.g. GreenGrid or U40108DL2022PTC410123"
          data-testid={`${testId}-input`}
          className="w-full bg-white border border-navy/15 text-ink pl-9 pr-9 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all placeholder-ink-faint rounded-md"
        />
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
        {loading ? (
          <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted animate-spin" />
        ) : selectedLabel ? (
          <CheckCircle2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600" />
        ) : null}
      </div>
      {selectedLabel ? (
        <div className="text-[11px] text-emerald-700 mt-1 font-mono" data-testid={`${testId}-selected`}>
          {selectedLabel}
        </div>
      ) : null}

      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-white border border-navy/15 rounded-md shadow-[0_18px_48px_rgba(15,61,46,0.18)] max-h-72 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-3 text-sm text-ink-muted flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Searching MCA…
            </div>
          )}
          {!loading && candidates.length === 0 && !query && recents.length > 0 && (
            <>
              <div className="px-3 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-widest text-ink-muted" data-testid={`${testId}-recents-header`}>
                Recent picks
              </div>
              {recents.map((c) => (
                <button
                  key={`recent-${c.cin}`}
                  type="button"
                  onClick={() => pickCandidate(c)}
                  data-testid={`${testId}-recent-${c.cin}`}
                  className="w-full text-left px-3 py-2.5 border-b border-navy/[0.06] last:border-0 hover:bg-gold/5 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <History size={14} className="mt-0.5 text-ink-muted shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-heading text-ink truncate">{c.company_legal_name}</div>
                      <div className="text-[11px] font-mono text-ink-muted mt-0.5">
                        {c.cin}
                        {c.date_of_incorporation ? ` · Inc. ${c.date_of_incorporation}` : ""}
                        {c.registered_state ? ` · ${c.registered_state}` : ""}
                        {c.company_status ? ` · ${c.company_status}` : ""}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
          {!loading && candidates.length === 0 && (query || recents.length === 0) && (
            <div className="px-3 py-3 text-sm text-ink-muted" data-testid={`${testId}-empty`}>
              {query ? "No matches. You can paste the CIN directly or continue with manual entry." : "Type a company name (3+ chars) to search MCA records."}
            </div>
          )}
          {!loading && candidates.map((c) => (
            <button
              key={c.cin}
              type="button"
              onClick={() => pickCandidate(c)}
              data-testid={`${testId}-option-${c.cin}`}
              className="w-full text-left px-3 py-2.5 border-b border-navy/[0.06] last:border-0 hover:bg-gold/5 transition-colors"
            >
              <div className="flex items-start gap-2">
                <Building2 size={14} className="mt-0.5 text-[#8A6600] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-heading text-ink truncate">{c.company_legal_name}</div>
                  <div className="text-[11px] font-mono text-ink-muted mt-0.5">
                    {c.cin}
                    {c.date_of_incorporation ? ` · Inc. ${c.date_of_incorporation}` : ""}
                    {c.registered_state ? ` · ${c.registered_state}` : ""}
                    {c.company_status ? ` · ${c.company_status}` : ""}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
